import styled from 'styled-components';
import { useMetricData } from './useMetricData';
import React, { useEffect, useRef, useState } from 'react';
import {
  MAX_METRIC_CARD_COLUMN,
  MAX_METRIC_CARD_ROW,
  METRIC_CARD_HEIGHT,
  MIN_METRIC_CARD_WIDTH,
} from './utils';
import {
  defaultNumberFormatOptions,
  formatValueWithMeasureFormat,
  globalFormat,
} from '../../../utils/formatting';
import { assertNotNil, isNotNil } from '../../../utils/types';
import { OVERALL_SLICE_KEY } from '../constants/utils';
import {
  getMeasureColorFromColorConfig,
  getSliceColorFromColorConfig,
} from '../utils/analysisColorConfig';
import { ExpressionSettingLineTypeView } from '../../RollingControlDisplay';
import { comparisonConfigDisplay } from '../../../utils/comparisonConfig';
import { debounce } from 'lodash';
import { theme } from '../../../styles/theme';
import useCacheMetricLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheMetricLlmRawDataEffect';
import PercentChangeLabel from '../../PercentChangeLabel';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
}

const METRIC_PADDING = 16;

const MetricContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-top: 1px solid ${theme.colors.border};
`;

const MetricRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.border};
`;

const MetricCard = styled.div<{
  $isComparison?: boolean;
  $width?: number;
}>`
  width: ${({ $width }) => $width ?? MIN_METRIC_CARD_WIDTH}px;
  min-height: ${METRIC_CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  padding: ${METRIC_PADDING}px;
  row-gap: ${({ $isComparison }) => ($isComparison ? 8 : 24)}px;
  justify-content: center;
  align-items: flex-start;
`;

const VerticalBorder = styled.div`
  border-right: 1px solid ${theme.colors.border};
`;

const MetricTitle = styled.div<{ $width?: number }>`
  display: flex;
  color: ${theme.colors.secondary800};
  font-size: 12px;
  font-weight: 400;
  height: 14px;
  column-gap: 4px;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  // padding on card container
  width: ${({ $width }) => ($width ? `${$width - 32}px` : 'auto')};
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TitleGroupSquare = styled.span<{ $color?: string }>`
  width: 8px;
  height: 8px;
  background-color: ${({ $color }) => $color ?? 'transparent'};
`;

const CardValue = styled.div<{ $width: number }>`
  color: ${theme.colors.secondary900};
  text-overflow: ellipsis;
  font-size: 32px;
  font-weight: 500;
  display: flex;
  align-items: center;
  line-height: normal;
  overflow-wrap: break-word;
  width: ${({ $width }) => $width}px;
`;

const LineTypeSymbol = styled.span`
  color: ${theme.colors.mainBlue};
`;

const MetricChart = ({ analysisId }: IProps) => {
  const metricContainerRef = useRef<HTMLDivElement>(null);
  const {
    displayedMeasures,
    displayedSlices,
    colorConfig,
    metadata: { comparisonInfo },
    settings: { chartSettings },
  } = useChartContext();
  const [containerWidth, setContainerWidth] = useState<number>();

  const { expressionSettings } = chartSettings;
  const { data, cardWidthByRow, hasHiddenMetrics } =
    useMetricData(containerWidth);
  const hasGroupsRatherThanOverall = displayedSlices.some(
    (slice) => slice.key !== OVERALL_SLICE_KEY,
  );

  // update container width by useDebounce for 1 second
  useEffect(() => {
    const updateWidth = debounce(() => {
      if (metricContainerRef.current?.clientWidth) {
        const newWidth =
          metricContainerRef.current.getBoundingClientRect().width;
        setContainerWidth(newWidth);
      }
    }, 200);
    if (!containerWidth && metricContainerRef.current?.clientWidth) {
      setContainerWidth(metricContainerRef.current?.clientWidth);
    } else {
      updateWidth();
    }

    return () => {
      updateWidth.cancel();
    };
  }, [metricContainerRef.current?.clientWidth, containerWidth]);

  useCacheMetricLlmRawDataEffect({
    analysisId,
    data,
  });

  return (
    <MetricContainer ref={metricContainerRef}>
      {data.map((cardRow, rowIndex) => (
        <MetricRowContainer key={rowIndex}>
          {cardRow.map((card, index) => {
            const displayedMeasure = displayedMeasures.find(
              (measure) => measure.key === card.measureKey,
            );
            const displayedSlice = displayedSlices.find(
              (slice) => slice.key === card.sliceKey,
            );
            assertNotNil(displayedMeasure);
            assertNotNil(displayedSlice);
            const squareColor = hasGroupsRatherThanOverall
              ? getSliceColorFromColorConfig(colorConfig, displayedSlice.key)
              : getMeasureColorFromColorConfig(
                  colorConfig,
                  displayedMeasure.key,
                );
            return (
              <React.Fragment key={card.key}>
                {index !== 0 && <VerticalBorder />}
                <MetricCard
                  $isComparison={!!comparisonInfo?.comparisonConfig}
                  $width={cardWidthByRow[rowIndex]}
                >
                  <MetricTitle $width={cardWidthByRow[rowIndex]}>
                    <TitleGroupSquare $color={squareColor} />
                    {hasGroupsRatherThanOverall ? (
                      <span>{`${displayedMeasure.label} ${displayedMeasure.name} - ${displayedSlice.name}`}</span>
                    ) : (
                      <span>{`${displayedMeasure.label} ${displayedMeasure.name}`}</span>
                    )}
                  </MetricTitle>
                  <CardValue
                    $width={cardWidthByRow[rowIndex] - METRIC_PADDING * 2}
                  >
                    <span>
                      {formatValueWithMeasureFormat(
                        card.displayValue,
                        displayedMeasure.format,
                      )}
                    </span>

                    {expressionSettings?.lineType && (
                      <LineTypeSymbol>
                        <ExpressionSettingLineTypeView
                          lineType={expressionSettings?.lineType}
                          displaySize="lg"
                        />
                      </LineTypeSymbol>
                    )}
                  </CardValue>
                  {/* card.percentChange could be NaN or undefined value */}
                  {isNotNil(card.percentageChange) &&
                    !Number.isNaN(card.percentageChange) &&
                    comparisonInfo?.comparisonConfig && (
                      <>
                        <div>
                          {card.compareValue && (
                            <b>
                              {formatValueWithMeasureFormat(
                                card.compareValue,
                                displayedMeasure.format,
                              )}
                            </b>
                          )}{' '}
                          in{' '}
                          {comparisonConfigDisplay(
                            comparisonInfo.comparisonConfig,
                          )}
                        </div>
                        <PercentChangeLabel
                          percentageChange={card.percentageChange}
                          compareTo={0}
                        >
                          {globalFormat.formatNumber(
                            card.percentageChange,
                            defaultNumberFormatOptions.percent,
                          )}
                        </PercentChangeLabel>
                      </>
                    )}
                </MetricCard>
              </React.Fragment>
            );
          })}
        </MetricRowContainer>
      ))}
      {hasHiddenMetrics && (
        <div>
          Some metrics may be hidden because of the browser width. You can
          adjust the browser size or modify the displayed metrics to view up to{' '}
          {MAX_METRIC_CARD_COLUMN * MAX_METRIC_CARD_ROW}.
        </div>
      )}
    </MetricContainer>
  );
};

export default MetricChart;
